import { useDispatch } from "react-redux";
import { useHistory } from 'react-router-dom';
import { setChangingChannels, setUser } from "../../../store/actions/authentication";
import { AuthType, Channel, SingleSignOnTypes } from "../../../types/working-model";
import { ChannelAvatar } from "./header";
import images from '../../../images';
import classNames from 'classnames';
import NavigationDropdown from "../../general-ui/dropdown/navigation-dropdown";
import './sidebar-nav.scss';
import { PATHNAMES } from "../../../utils/admin-routing-utils";
import { setSelectedEventGroup } from '../../../store/actions/admin/events';
import { useTypedSelector } from "../../../store/reducers/use-typed-selector";
import { handleSSOSignIn, handleOauth2Siginin } from "../../../utils/sso-utils";
import { SelectChannel } from "../../../connection/auth";

interface AdminNavProps {
	currentChannel?: Channel;
}

export default function ChannelSelectionDropdown({ currentChannel }: AdminNavProps): JSX.Element {
	const eventsLoading = useTypedSelector(state => state.EventsReducer.eventsLoading);
	const changingChannels = useTypedSelector(state => state.AuthReducer.changingChannels);
	const channels = useTypedSelector(state => state.AuthReducer.channels);
	const token = useTypedSelector(state => state.AuthReducer.token);
	const user = useTypedSelector(state => state.AuthReducer.user);
	const dispatch = useDispatch();
	const history = useHistory();

	if (!user) return <></>;

	async function selectChannel(destinationChannel: Channel) {
		if (changingChannels || eventsLoading || !user) return;

		// Do nothing if already on the clicked channel
		const channelId = destinationChannel.channel;
		if (channelId === user?.active_channel) return;

		const { auth_integration, auth_type } = destinationChannel;
		const isAdminSsoEnabled = !!user.channels_using_sso?.includes(channelId);

		// Admin should use SSO
		if (isAdminSsoEnabled && auth_type?.includes(AuthType.sso)) {
			// OAuth2
			if (auth_integration?.type === SingleSignOnTypes.OAUTH2) {
				return handleOauth2Siginin(channelId)();
			}

			// SAML/OIDC
			if (auth_integration?.type === SingleSignOnTypes.OIDC || auth_integration?.type === SingleSignOnTypes.SAML) {
				return handleSSOSignIn(auth_integration, channelId)();
			}
		}

		// Invalid state but is technically possible
		if (auth_type !== null && !auth_type?.includes(AuthType.email_password)) return;

		// Email / password path
		if (token) {
			dispatch(setChangingChannels(true));
			const updatedToken = await SelectChannel(token, channelId);
			dispatch(setUser(updatedToken));
			dispatch(setSelectedEventGroup(''));
			dispatch(setChangingChannels(false));
			history.push(PATHNAMES.Dashboard);
		}
	}

	return (
		<NavigationDropdown
			title={<>
				{currentChannel
					? <ChannelAvatar channel={currentChannel} />
					: <img src={images.HeaderImage} alt={"Brandlive"} />
				}
			</>}
			isArrow={false}
			className="channels"
		>
			{channels.map((channel) => (
				<div
					className={classNames("channel-item", { on: channel.channel === user.active_channel, disabled: changingChannels || eventsLoading })}
					key={channel.channel}
					onClick={() => selectChannel(channel)}
				>
					<ChannelAvatar channel={channel} />
					<label>{channel.name}</label>
				</div>
			))}
		</NavigationDropdown>
	);
}
